import {useEffect, useMemo, useState} from "react";

import {FeatureKey} from "@/types/generated/FeatureKey";
import {hasFeature} from "@hooks/useFeatures";
import {UserPermission} from "@/types/generated/UserPermission";
import {hasPermission} from "@hooks/usePermissions";
import {setSideBarColors} from "@/containers/Theme/ThemeHelper";

import organizationClient from "@apis/organizationClient";

import useAvailibleMenu from "./useAvailibleMenu";
import {type AvailibleMenuData, type MenuRoute, type MenuType, MenuTypeConst} from "./menuTypes";
import type {LoginInfo, LoginOrganisationInfo, Partner} from "@/types/LoginInfo";
import {LanguageKey} from "@/types/generated/LanguageKey";
import {translate} from "@hooks/useLanguage";

/**
 * Extended organization info that includes partner data from API response
 */
interface ExtendedOrganisationInfo extends LoginOrganisationInfo {
  partner?: Partner;
}

/**
 * Extended menu route with additional fields specific to admin menu
 */
interface ExtendedMenuRoute extends MenuRoute {
  param?: string;
  title?: string;
  views?: ExtendedMenuRoute[];
}

/**
 * Return type of useSelectedOrgMenu hook
 */
interface AdminMenuData extends AvailibleMenuData {
  type: MenuType;
  loading: boolean;
  routes: ExtendedMenuRoute[];
}

/**
 * Hook for managing the admin menu routes based on selected organization
 *
 * @param loginInfo - Current user's login information including features and permissions
 * @param orgId - Optional organization ID to load specific organization context
 * @returns Menu data including routes, available paths, and loading state
 */
const useSelectedOrgMenu = (
  loginInfo: LoginInfo,
  orgId?: number
): AdminMenuData => {
  const { custumInfo, organisation } = loginInfo;
  const [orgSelected, setOrgSelected] = useState<ExtendedOrganisationInfo | null>(null);
  // The org id that `orgSelected` has finished resolving for (set even when a
  // fetch fails), so the "menu caught up to the URL org" check below can't get
  // stuck loading if organizationClient.findOne rejects.
  const [resolvedOrgId, setResolvedOrgId] = useState<number | null>(null);

  const isSystem = organisation?.organisationType === "SYSTEM";
  const isPartnerOrg = orgSelected?.organisationType === "PARTNER";

  const routes = useMemo(() => {
    return [
      ...(isSystem
        ? [
            {
              collapse: false,
              view: true,
              name: isPartnerOrg
                ? translate(LanguageKey.partner_details_key)
                : translate(LanguageKey.organization_details_key),
              state: "orgDetailDashboard",
              to: "/organisation_dashboard",
              layout: "",
              icon: <span className="dashboard-menu-icon menu-icon"></span>,
              views: [],
              topOverview: true,
            },
          ]
        : []),
      {
        collapse: true,
        name: translate(LanguageKey.trackers_and_sensors_key),
        state: "openTrackersSensors",
        views: [
          {
            view: hasFeature(FeatureKey.factory_upload),
            to: "/factory_upload",
            layout: "",
            name: translate(LanguageKey.sidebar_factory_upload_key),
            subPages: ["/factory_upload_update", "/factory_order_create"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_TRACKERS),
            to: "/trackers_admin",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_TRACKERS_description_key),
            subPages: [
              "/trackers_admin_update",
              "/import_tracker_settings_admin",
            ],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_BLE_SENSOR),
            to: "/ble_sensor_list",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_BLE_SENSOR_description_key),
            subPages: ["/ble_sensor_update", "/ble_sensor_import"],
          },
          {
            view:
              orgSelected?.organisationType !== "SYSTEM" &&
              orgSelected?.organisationType !== "PARTNER" &&
              hasPermission(UserPermission.MENU_ADMIN_ASSET_TYPES),
            to: "/asset_types",
            layout: "",
            name: translate(
              LanguageKey.UserPermission_MENU_ADMIN_ASSET_TYPES_description_key
            ),
          },
          {
            // STIC-15615 R1: NORMAL logins keep their own page (view shared profiles);
            // SYSTEM/PARTNER cross-level browsing hides it for NORMAL orgs — they can
            // never be granted the usage_profile feature (Features.visibleFor), so the
            // "no access" page would be a permanent dead-end for them. Partner orgs
            // without the key still get the page + banner (the key IS enablable there).
            view:
              hasPermission(UserPermission.MENU_ADMIN_USAGE_PROFILE) &&
              (organisation?.organisationType === "NORMAL" ||
                orgSelected?.organisationType !== "NORMAL"),
            to: "/device_usage_profile_admin",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_USAGE_PROFILE_description_key),
            subPages: ["/device_usage_profile_manage_admin", "/device_usage_profile_compare_admin"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_EOL_TRACKERS),
            to: "/end_of_life",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_EOL_TRACKERS_description_key),
          },
          {
            view:
              hasFeature(FeatureKey.bulk_operations) &&
              hasPermission(UserPermission.MENU_ADMIN_BULK_OPERATION),
            to: "/bulk_manage_admin",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_BULK_OPERATION_description_key),
            subPages: ["/bulk_results_detail", "/bulk_operations_admin"],
          },
          {
            view:
              orgSelected?.organisationType !== "SYSTEM" &&
              (hasFeature(FeatureKey.diagnostic_tab) ||
                hasFeature(FeatureKey.diagnostic_organisation)) &&
              hasPermission(UserPermission.MENU_ADMIN_DIAGNOSTIC),
            to: "/diagnostic_organisation",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_DIAGNOSTIC_description_key),
          },
          {
            view:
              (orgSelected?.organisationType === "SYSTEM" ||
                // STIC-15615 R8: the battery calculator is a tool of the
                // partner's own (Classic) experience — browsing a partner org
                // in cross-level mode doesn't get it
                (orgSelected?.organisationType === "PARTNER" &&
                  orgSelected?.id === organisation?.id)) &&
              hasPermission(UserPermission.MENU_ADMIN_BATTERY_CALCULATOR),
            to: "/battery_calculator",
            layout: "",
            name: translate(LanguageKey.battery_calc_battery_calculator_key),
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_TRACKER_PRODUCT),
            to: "/tracker_product",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_TRACKER_PRODUCT_description_key),
            subPages: ["/tracker_product_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_PRODUCT_AUTO_UPGRADE),
            to: "/product_auto_upgrade",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_PRODUCT_AUTO_UPGRADE_description_key),
            subPages: ["/product_auto_upgrade_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_PRODUCT_REGEXP_FILTER),
            to: "/product_regexp_filter",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_PRODUCT_REGEXP_FILTER_description_key),
            subPages: ["/product_regexp_filter_detail"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.location_key),
        state: "openLocation",
        views: [
          {
            view:
              orgSelected?.organisationType !== "SYSTEM" &&
              hasFeature(FeatureKey.geozone_manager) &&
              hasPermission(UserPermission.MENU_ADMIN_GEOZONES),
            to: "/areas",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_GEOZONES_description_key),
            subPages: ["/areas_update", "/import_export_geozones"],
          },
          {
            view:
              orgSelected?.organisationType !== "SYSTEM" &&
              hasFeature(FeatureKey.metar_weather_location) &&
              hasPermission(UserPermission.MENU_ADMIN_METAR_WEATHER_LOCATION),
            to: "/metar_weather_location",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_METAR_WEATHER_LOCATION_description_key),
            subPages: ["/metar_weather_location_manage"],
          },
          {
            view:
              hasFeature(FeatureKey.loc_geolayer) && hasPermission(UserPermission.MENU_ADMIN_GEOLAYER),
            to: "/geolayers",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_GEOLAYER_description_key),
            subPages: ["/geolayer_update"],
          },
          {
            view:
              orgSelected?.organisationType !== "SYSTEM" &&
              hasFeature(FeatureKey.loc_site_map) && hasPermission(UserPermission.MENU_ADMIN_SITE_MAP),
            to: "/site_maps",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_SITE_MAP_description_key),
            subPages: ["/site_map_update"],
          },
          {
            view:
              hasFeature(FeatureKey.loc_beacon) &&
              hasPermission(UserPermission.MENU_ADMIN_INFRASTRUCTURE),
            to: "/infrastructures_admin",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_INFRASTRUCTURE_description_key),
            subPages: ["/infrastructures_update", "/import_infrastructure_settings_admin"],
          },
          {
            view:
              hasFeature(FeatureKey.private_wifi_resolution) &&
              hasPermission(UserPermission.MENU_ADMIN_WIFI),
            to: `/wifi`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_WIFI_description_key),
            subPages: ["/wifi/blocked", "/wifi/unresolved"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_PRIVATE_CELLULAR),
            to: `/private_cellular`,
            layout: "",
            name: translate(LanguageKey.cellular_tower_adjustments_key),
          },
          {
            // STIC-15615 R11: location settings configure ONE normal org and
            // never inherit downwards. Classic partner (own org) hides the
            // item; a partner org sliced in cross-level mode keeps it visible
            // but greyed — the page explains why.
            view:
              orgSelected?.organisationType !== "SYSTEM" &&
              !(orgSelected?.organisationType === "PARTNER" &&
                orgSelected?.id === organisation?.id) &&
              (hasPermission(UserPermission.MENU_ADMIN_LOCATION_SETTINGS) ||
                hasPermission(UserPermission.GEOZONE_BEHAVIOR_VIEW) ||
                hasPermission(UserPermission.JOURNEY_ALGO_SETTINGS_VIEW)),
            disabled: orgSelected?.organisationType === "PARTNER",
            to: "/location_settings",
            param: hasPermission(UserPermission.BLE_TAG_TRACKING_SETTINGS_MANAGE)
              ? "type=edit"
              : "type=view",
            layout: "",
            name: translate(LanguageKey.location_settings_key),
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasFeature(FeatureKey.loc_beacon) &&
              hasPermission(UserPermission.MENU_ADMIN_GEOLAYER_GENERATOR),
            to: "/geolayer_generator",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_GEOLAYER_GENERATOR_description_key),
            subPages: ["/geolayer_generator_manage"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_HARVESTED_CELLULAR),
            to: "/harvested_cellular",
            layout: "",
            name: translate(LanguageKey.harvested_cell_towers_key),
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_CELL_TOWER_TROUBLESHOOTING),
            to: "/wifi_cellular_mismatch",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_CELL_TOWER_TROUBLESHOOTING_description_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.automation_key),
        state: "openAutomationRule",
        views: [
          {
            view:
              orgSelected?.organisationType !== "SYSTEM" &&
              hasFeature(FeatureKey.automation_manager) &&
              hasPermission(UserPermission.MENU_ADMIN_AUTOMATION),
            to: "/automation_rule",
            layout: "",
            name: translate(LanguageKey.rules_key),
            subPages: ["/automation_rule_create", "/automation_rule_update"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.tags_key),
        state: "openTags",
        views: [
          {
            view:
              orgSelected?.organisationType !== "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_ASSET_TAGS),
            to: "/asset_tags_manage",
            subPages: ["/update_device_tag"],
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_ASSET_TAGS_description_key),
          },
          {
            view:
              orgSelected &&
              "PARTNER" === orgSelected.organisationType &&
              hasPermission(UserPermission.MENU_ADMIN_PARTNER_TAGS),
            isForceAvailableMenu: true,
            to: "/partner_tag",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_PARTNER_TAGS_description_key),
            subPages: ["/partner_tag_manage"],
          },
          {
            view:
              orgSelected &&
              "SYSTEM" === orgSelected.organisationType &&
              hasPermission(UserPermission.MENU_ADMIN_SYSTEM_TAGS),
            isForceAvailableMenu: true,
            to: "/system_tag",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_SYSTEM_TAGS_description_key),
            subPages: ["/system_tag_manage"],
          },
          {
            view:
              orgSelected?.organisationType !== "SYSTEM" &&
              hasFeature(FeatureKey.geozone_manager) &&
              hasPermission(UserPermission.MENU_ADMIN_GEOZONE_TAGS),
            to: "/geozone_tags_manage",
            subPages: ["/update_geozone_tag"],
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_GEOZONE_TAGS_description_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.insights_key),
        state: "openInsights",
        views: [
          {
            view:
              orgSelected &&
              "SYSTEM" !== orgSelected.organisationType &&
              hasFeature(FeatureKey.alert_ruleconf) &&
              hasPermission(UserPermission.MENU_ADMIN_ALERT_RULE),
            isForceAvailableMenu: true,
            to: "/manage_alerts",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_ALERT_RULE_description_key),
            subPages: ["/manage_alerts_update"],
          },
          {
            view:
              orgSelected?.organisationType !== "SYSTEM" &&
              hasFeature(FeatureKey.utilization_basic) &&
              hasPermission(UserPermission.MENU_ADMIN_DEVICE_UTILIZATION_SCHEME),
            to: "/utilization_scheme",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_DEVICE_UTILIZATION_SCHEME_description_key),
            subPages: ["/utilization_scheme_update"],
          },
          {
            view:
              orgSelected?.organisationType !== "SYSTEM" &&
              hasFeature(FeatureKey.flow_definition) &&
              hasPermission(UserPermission.MENU_ADMIN_FLOW) &&
              !orgSelected?.partner?.commercialPartner,
            to: "/flow_definition",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_FLOW_description_key),
            subPages: ["/flow_settings"],
          },
          {
            view:
              orgSelected &&
              "NORMAL" === orgSelected.organisationType &&
              hasFeature(FeatureKey.process_manager) &&
              hasPermission(UserPermission.MENU_ADMIN_PROCESS_DEFINITION),
            isForceAvailableMenu: true,
            to: "/process_definition",
            layout: "",
            title:
              translate(LanguageKey.UserPermission_MENU_ADMIN_PROCESS_DEFINITION_description_key),
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_PROCESS_DEFINITION_description_key),
            subPages: ["/process_definition_update"],
          },
          {
            view:
              orgSelected &&
              "NORMAL" === orgSelected.organisationType &&
              hasFeature(FeatureKey.maintenance_basic) &&
              hasPermission(UserPermission.MENU_ADMIN_MAINTENANCE),
            isForceAvailableMenu: true,
            to: "/maintenance_programs",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_MAINTENANCE_description_key),
            subPages: [
              "/maintenance_programs_manage",
              "/maintenance_programs_bulk_start",
            ],
          },
          {
            view:
              hasFeature(FeatureKey.dashboard) && hasPermission(UserPermission.MENU_ADMIN_DASHBOARD),
            to: "/bi_dashboards",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_DASHBOARD_description_key),
            subPages: ["/bi_dashboard_manage"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.ai_key),
        state: "openAi",
        views: [
          {
            view: hasFeature(FeatureKey.agents) && hasPermission(UserPermission.MENU_ADMIN_AGENT),
            to: "/enrolled_agents",
            layout: "",
            name: translate(LanguageKey.agents_section_title_key),
            // The agent detail page, so the entry stays highlighted once you open
            // an agent from the list. Named by its prefix — subPage matching is
            // prefix-based, and the route carries the agent name.
            //
            // Deliberately not "/enrolled_agents" itself, which would cover the
            // same route and read simpler: SidebarBody skips defaulting ?org= for
            // a path listed here verbatim, so listing the list's own path would
            // break deep links that arrive without an org.
            subPages: ["/enrolled_agents/registered"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.access_management_key),
        state: "openAccess",
        views: [
          {
            view: hasPermission(UserPermission.MENU_ADMIN_USERS),
            to: "/users_admin",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_USERS_description_key),
            subPages: ["/users_admin_update"],
          },
          {
            view:
              hasFeature(FeatureKey.custom_user_roles) &&
              hasPermission(UserPermission.MENU_ADMIN_USER_ROLE),
            to: "/role_manager_admin",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_USER_ROLE_description_key),
            subPages: ["/role_manager_admin_update"],
          },
          {
            view:
              hasFeature(FeatureKey.visibility_filters) &&
              hasPermission(UserPermission.MENU_ADMIN_VISIBILITY_FILTER),
            to: "/manage_visibility_filters",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_VISIBILITY_FILTER_description_key),
            subPages: ["/manage_visibility_filters_update"],
          },
          {
            view:
              orgSelected &&
              ("PARTNER" === loginInfo.organisation?.organisationType ||
                "SYSTEM" === loginInfo.organisation?.organisationType) &&
              "PARTNER" === orgSelected.organisationType &&
              hasFeature(FeatureKey.organisation_visibility_filters) &&
              hasPermission(UserPermission.MENU_ADMIN_ORGANISATION_VISIBILITY_FILTER),
            isForceAvailableMenu: true,
            to: "/manage_organisation_filters",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_ORGANISATION_VISIBILITY_FILTER_description_key),
            subPages: ["/manage_organisation_filters_update"],
          },
          {
            view:
              hasFeature(FeatureKey.int_sso) &&
              hasPermission(UserPermission.MENU_ADMIN_ORGANIZATION_SSO) &&
              (organisation?.organisationType !== "PARTNER" ||
                hasFeature(FeatureKey.partner_org_manage)),
            to: `/single_sign_on`,
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_ORGANIZATION_SSO_description_key),
          },
          {
            view:
              hasFeature(FeatureKey.audit_log) &&
              hasPermission(UserPermission.MENU_ADMIN_AUDIT_LOG),
            to: `/audit_log`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_AUDIT_LOG_description_key),
            subPages: ["/audit_log_view", "/audit_log_aggregated_view"],
          },
          {
            view:
              // STIC-15615 R10: own-level "System" ONLY — no cross-level and
              // no org-scoped variant anymore
              orgSelected?.organisationType === "SYSTEM" &&
              hasFeature(FeatureKey.stock_movement) &&
              hasPermission(UserPermission.MENU_STOCK_MOVEMENT),
            // The GLOBAL list (same data the old "All" entry showed) — the
            // org-scoped variant is gone entirely
            to: `/stock_movement`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_STOCK_MOVEMENT_description_key),
            subPages: ["/stock_movement_view"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.OAUTH2_CLIENT_VIEW),
            to: "/oauth2_registered_client",
            layout: "",
            name: translate(LanguageKey.oauth2_client_management_key),
            subPages: [
              "/oauth2_registered_client_update",
              "/oauth2_registered_client_create",
            ],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.organizations_key),
        state: "openSystemOrganizations",
        views: [
          // STIC-15615 R2: Organizations and Demos are own-level artifacts (all
          // owned by System) — moved here from the cross-level "All" menu
          // (useAllMenu), next to Partners which already lives at this level.
          // Included for SYSTEM logins ONLY (not merely view-gated): these paths
          // also live in usePartnerMenu, and getMenuByLocationPath resolves
          // selectedOrgMenu.allPaths BEFORE partnerMenu — registering them here
          // for a PARTNER login would hijack its "All" tab (/organization is
          // the partner menu's default path). The partner own-level mirror of
          // this move belongs to STIC-15365 (partner navigation rework).
          ...(isSystem
            ? [
                {
                  view:
                    orgSelected?.organisationType === "SYSTEM" &&
                    hasPermission(UserPermission.MENU_ADMIN_ORGANIZATION_LIST),
                  to: "/organization",
                  layout: "",
                  name: translate(LanguageKey.UserPermission_MENU_ADMIN_ORGANIZATION_LIST_description_key),
                  subPages: ["/organization_update"],
                },
                {
                  view:
                    orgSelected?.organisationType === "SYSTEM" &&
                    hasFeature(FeatureKey.demo_environment) &&
                    hasPermission(UserPermission.MENU_ADMIN_DEMO),
                  to: "/org_demo",
                  layout: "",
                  name: translate(LanguageKey.UserPermission_MENU_ADMIN_DEMO_description_key),
                  subPages: ["/org_demo_manage"],
                },
              ]
            : []),
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_PARTNERS),
            to: "/partners",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_PARTNERS_description_key),
            subPages: ["/partners_update", "/partners_create"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_INTERNAL_PLATFORM_PLAN),
            to: "/internal_plans",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INTERNAL_PLATFORM_PLAN_description_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.organisation_billing_key),
        state: "openSystemBilling",
        views: [
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_INVOICING_OPERATIONS),
            to: "/invoicing_operations",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INVOICING_OPERATIONS_description_key),
            subPages: ["/invoicing_operations_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_INVOICES),
            to: "/invoices",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INVOICES_description_key),
            subPages: ["/invoices_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_RENEWAL_FORECASTS),
            to: "/quotation",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_RENEWAL_FORECASTS_description_key),
            subPages: ["/quotation_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_PRICELIST),
            to: "/price_lists",
            layout: "",
            name: translate(LanguageKey.price_list_key),
            subPages: ["/price_list_update", "/price_list_items"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_PRICE_BOOKS),
            to: "/pricebook",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_PRICE_BOOKS_description_key),
            subPages: ["/pricebook_update", "/pricebook_increase"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_PRICE_BOOKS),
            to: "/customer",
            layout: "",
            name: translate(LanguageKey.customers_key),
            subPages: ["/customer_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
            to: "/consolidated_customer",
            layout: "",
            name: translate(LanguageKey.consolidated_customers_key),
            subPages: ["/consolidated_customer_update"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.organization_settings_key),
        state: "openOrgSetting",
        views: [
          {
            view:
              hasPermission(UserPermission.MENU_ADMIN_ORGANIZATION_DETAILS) &&
              (organisation?.organisationType !== "PARTNER" ||
                hasFeature(FeatureKey.partner_org_manage)),
            to: `/organization_update`,
            param: hasPermission(UserPermission.SYSTEM_ADMIN_VIEW)
              ? "type=edit"
              : "type=view",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_ORGANIZATION_DETAILS_description_key),
          },
          {
            view:
              hasPermission(UserPermission.MENU_ADMIN_PLATFORM_PLAN) &&
              (organisation?.organisationType !== "PARTNER" ||
                hasFeature(FeatureKey.partner_org_manage)),
            to: `/product_plan`,
            param: hasPermission(UserPermission.SYSTEM_ADMIN_VIEW)
              ? "type=edit"
              : "type=view",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_PLATFORM_PLAN_description_key),
          },
          {
            view:
              orgSelected &&
              hasFeature(FeatureKey.quote_manage) &&
              hasPermission(UserPermission.MENU_ADMIN_QUOTES) &&
              ("PARTNER" === orgSelected.organisationType
                ? (orgSelected.partner?.commercialPartner ||
                    loginInfo.partner?.commercialPartner) &&
                  (orgSelected.partnerPriceList === "EU" ||
                    loginInfo.organisation?.partnerPriceList === "EU")
                : "NORMAL" === orgSelected.organisationType &&
                  (loginInfo.organisation?.organisationType !== "PARTNER" ||
                    (!!loginInfo.partner?.commercialPartner &&
                      loginInfo.organisation?.partnerPriceList === "EU"))),
            isForceAvailableMenu: true,
            to: `/quotes_storefront_partner`,
            layout: "",
            name: translate(LanguageKey.quotes_key),
            subPages: ["/quotes_partner", "/quote_detail_partner"],
          },
          {
            view:
              orgSelected &&
              hasFeature(FeatureKey.show_invoicing) &&
              hasPermission(UserPermission.MENU_ADMIN_BILLING) &&
              "SYSTEM" !== orgSelected.organisationType,
            isForceAvailableMenu: true,
            to: `/invoicing`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_BILLING_description_key),
            subPages: ["/invoice_detail", "/quotation_detail"],
          },
          {
            view:
              orgSelected &&
              hasPermission(UserPermission.MENU_ADMIN_PARTNER_DETAIL) &&
              "SYSTEM" !== orgSelected.organisationType,
            isForceAvailableMenu: true,
            to: "/partners_details",
            param: "type=view&id=&tabActive=general_tab",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_PARTNER_DETAIL_description_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.customization_key),
        state: "opencustomization",
        views: [
          {
            view:
              hasFeature(FeatureKey.landing_page) &&
              hasPermission(UserPermission.MENU_ADMIN_LANDING_PAGE) &&
              (organisation?.organisationType !== "PARTNER" ||
                hasFeature(FeatureKey.partner_org_manage)),
            to: `/default_landing_page`,
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_LANDING_PAGE_description_key),
          },
          {
            view:
              hasFeature(FeatureKey.report_generator) &&
              hasPermission(UserPermission.MENU_ADMIN_REPORT_TEMPLATE),
            to: "/report_template_library",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_REPORT_TEMPLATE_description_key),
            subPages: ["/report_template_library_manage"],
          },
          {
            view:
              hasFeature(FeatureKey.image_library) &&
              hasPermission(UserPermission.MENU_ADMIN_IMAGE_LIBRARY),
            to: "/image_library",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_IMAGE_LIBRARY_description_key),
            subPages: ["/image_library_manage"],
          },
          {
            view:
              hasFeature(FeatureKey.custom_i18n_message) &&
              hasPermission(UserPermission.MENU_ADMIN_CUSTOM_I18N_MESSAGE),
            to: "/custom_i18n_messages",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_CUSTOM_I18N_MESSAGE_description_key),
            subPages: ["/custom_i18n_messages_manage"],
          },
          {
            view:
              orgSelected?.organisationType !== "SYSTEM" &&
              hasFeature(FeatureKey.custom_interface) &&
              hasPermission(UserPermission.MENU_ADMIN_CUSTOM_INTERFACE) &&
              (organisation?.organisationType !== "PARTNER" ||
                hasFeature(FeatureKey.partner_org_manage)),
            to: `/custom_branding`,
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_CUSTOM_INTERFACE_description_key),
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_SUPPORT),
            to: `/support`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_SUPPORT_description_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.developers_key),
        state: "openUtilization",
        views: [
          {
            view: hasPermission(UserPermission.MENU_ADMIN_API_DOCS),
            to: "/api-docs",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_API_DOCS_description_key),
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_API_ACCESS),
            to: "/api-access",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_API_ACCESS_description_key),
            subPages: ["/api-access-update"],
          },
          {
            view:
              orgSelected?.organisationType !== "SYSTEM" &&
              hasFeature(FeatureKey.business_inject) &&
              hasPermission(UserPermission.MENU_ADMIN_BUSINESS_CHANNEL) &&
              // MENU_ADMIN_BUSINESS_CHANNEL declares BUSINESS_CHANNEL_VIEW as a
              // dependency, but the role editor only cascades dependencies when a
              // permission is checked, never when one is unchecked. Roles can
              // therefore keep the menu permission while the definition permissions
              // are revoked - check it explicitly here (STIC-15894).
              hasPermission(UserPermission.BUSINESS_CHANNEL_VIEW) &&
              (organisation?.organisationType !== "PARTNER" ||
                hasFeature(FeatureKey.partner_org_manage)),
            to: `/third_party_data`,
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_BUSINESS_CHANNEL_description_key),
            subPages: ["/channel_update"],
          },
          {
            view:
              hasFeature(FeatureKey.int_rest_mqtt_push) &&
              hasPermission(UserPermission.MENU_ADMIN_WEBHOOKS) &&
              (organisation?.organisationType !== "PARTNER" ||
                hasFeature(FeatureKey.partner_org_manage)),
            to: `/webhooks`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_WEBHOOKS_description_key),
            subPages: ["/callbacks_update"],
          },
          {
            view:
              hasFeature(FeatureKey.int_api_clientapp) &&
              hasPermission(UserPermission.MENU_ADMIN_CLIENT_APP_API),
            to: "/client-app-api",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_CLIENT_APP_API_description_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.sidebar_support_key),
        state: "openInsights",
        views: [
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
            to: "/failed_trackers",
            layout: "",
            name: translate(LanguageKey.failed_trackers),
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
            to: "/manufacturing",
            layout: "",
            name: translate(LanguageKey.v42_manufacturing_key),
            subPages: ["/manufacturing_order_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_STOCK),
            to: "/stock",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_STOCK_description_key),
            subPages: ["/stock_update"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_RMA_DECLARATION),
            to: `/rma_declaration_view`,
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_RMA_DECLARATION_description_key),
            subPages: ["/rma_declaration_view_details"],
          },
          {
            view:
              hasFeature(FeatureKey.zendesk_ticket) &&
              hasPermission(UserPermission.MENU_ADMIN_ZENDESK_SUPPORT_TICKET),
            to: "/support_tickets",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_ZENDESK_SUPPORT_TICKET_description_key),
          },
          {
            view:
              (hasFeature(FeatureKey.zendesk_ticket) &&
                hasPermission(UserPermission.SYSTEM_ADMIN_MANAGE)) ||
              hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
            to: "/zendesk_tickets",
            layout: "",
            name: translate(LanguageKey.zendesk_ticket_key),
          },
          {
            view:
              hasFeature(FeatureKey.zendesk_ticket) &&
              hasPermission(UserPermission.MENU_ADMIN_ZENDESK_ORDER),
            to: "/order_tickets",
            layout: "",
            name:
              translate(LanguageKey.UserPermission_MENU_ADMIN_ZENDESK_ORDER_description_key),
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_CSM),
            to: "/csm_info",
            layout: "",
            name: translate(LanguageKey.csm_info_menu_key),
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_CSM),
            to: "/csm_comment",
            layout: "",
            name: translate(LanguageKey.csm_comment_menu_key),
            subPages: ["/csm_comment_create", "/csm_comment_update"],
          },
          {
            view: hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
            to: "/internal_asset_classification",
            layout: "",
            name: translate(LanguageKey.internal_asset_classification_menu_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.platform_notifications_key),
        state: "openSystemPlatformNotifications",
        views: [
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_INTEGRATED_HELP),
            to: "/integrated_help",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INTEGRATED_HELP_description_key),
            subPages: ["/integrated_help_update"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
            to: "/doc-aliases",
            layout: "",
            name: translate(LanguageKey.doc_alias_overview_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.salesforce_integration_key),
        state: "openSalesforce",
        views: [
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_ACCOUNT),
            to: "/salesforce_account",
            layout: "",
            name: translate(LanguageKey.salesforce_accounts_key),
            subPages: ["/salesforce_account_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_CONTACT),
            to: "/salesforce_contact",
            layout: "",
            name: translate(LanguageKey.salesforce_contacts_key),
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_LEAD),
            to: "/salesforce_lead",
            layout: "",
            name: translate(LanguageKey.salesforce_leads_key),
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_OPPORTUNITY),
            to: "/salesforce_opportunity",
            layout: "",
            name: translate(LanguageKey.salesforce_opportunities_key),
            subPages: ["/salesforce_opportunity_line_items", "/salesforce_opportunity_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_QUOTE),
            to: "/salesforce_quote",
            layout: "",
            name: translate(LanguageKey.salesforce_quotes_key),
            subPages: ["/salesforce_quote_line_item", "/salesforce_quote_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_CHANNEL_PARTNER),
            to: "/salesforce_channel_partner",
            layout: "",
            name: translate(LanguageKey.salesforce_channel_partners_key),
            subPages: ["/salesforce_channel_partner_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_USER),
            to: "/salesforce_user",
            layout: "",
            name: translate(LanguageKey.salesforce_users_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.impartner_integration_key),
        state: "openImpartner",
        views: [
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_IMPARTNER_ACCOUNT),
            to: "/impartner_account",
            layout: "",
            name: translate(LanguageKey.impartner_accounts_key),
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_IMPARTNER_CHANNEL_PARTNER),
            to: "/impartner_channel_partner",
            layout: "",
            name: translate(LanguageKey.impartner_channel_partners_key),
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_IMPARTNER_USER),
            to: "/impartner_user",
            layout: "",
            name: translate(LanguageKey.impartner_users_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.communication_key),
        state: "openCommunication",
        views: [
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_SIGFOX_ACCOUNT),
            to: "/sigfox_account",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_SIGFOX_ACCOUNT_description_key),
            subPages: ["/sigfox_account_update", "/sigfox_account_device_type_info"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_CELLULAR_ACCOUNT),
            to: "/cellular_account",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_CELLULAR_ACCOUNT_description_key),
            subPages: [
              "/cellular_account_manage",
              "/cellular_service_profile_operators",
              "/cellular_service_profile_manage",
              "/cellular_network_operator_chart",
            ],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_CELLULAR_SIM),
            to: "/cellular_sim",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_CELLULAR_SIM_description_key),
            subPages: ["/cellular_sim_manage"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.api_key),
        state: "openSystemApi",
        views: [
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_INTERNAL_API),
            to: "/internal-api",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INTERNAL_API_description_key),
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_AI_PROMPTS),
            to: "/ai-prompts",
            layout: "",
            name: translate(LanguageKey.manage_ai_prompts_key),
            subPages: ["/ai-prompt-update"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_AI_CHATS),
            to: "/ai-chats",
            layout: "",
            name: translate(LanguageKey.manage_ai_chats_key),
            subPages: ["/ai-chat-view"],
          },
        ],
      },
    ];
  }, [loginInfo, orgSelected, organisation, isPartnerOrg, isSystem]);

  const [data, setData] = useState<AdminMenuData>({
    type: MenuTypeConst.ADMIN,
    loading: true,
    routes: [],
    availableMenuPaths: {},
    allPaths: {},
    defaultPath: "/trackers_admin",
  });

  const availibleMenuData = useAvailibleMenu(routes as MenuRoute[], "/trackers_admin");

  useEffect(() => {
    setData({ ...data, ...availibleMenuData, loading: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availibleMenuData]);

  useEffect(() => {
    if (custumInfo) {
      setSideBarColors(custumInfo);
    }
  }, [custumInfo]);

  useEffect(() => {
    setData({ ...data, loading: true });
    if (orgId) {
      if (
        loginInfo.organisation?.id !== orgId &&
        hasPermission(UserPermission.ORGANIZATION_VIEW)
      ) {
        organizationClient
          .findOne({ id: orgId })
          .then(({ data }) => setOrgSelected(data))
          .catch(() => setOrgSelected(null))
          .finally(() => setResolvedOrgId(orgId));
      } else {
        setOrgSelected(loginInfo.organisation ?? null);
        setResolvedOrgId(orgId);
      }
    } else {
      setOrgSelected(null);
      setResolvedOrgId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // The menu paths are derived from `orgSelected`, which is updated
  // asynchronously after `orgId` changes. Until it catches up, the returned
  // paths still reflect the PREVIOUS org — report loading so consumers
  // (SidebarBody) wait for the recompute instead of routing on stale paths
  // (which would e.g. bounce a freshly-set ?org= deep link to the default
  // admin page because the requested route isn't yet "visible").
  const orgNotReady = orgId != null && resolvedOrgId !== orgId;

  return orgNotReady ? { ...data, loading: true } : data;
};

export default useSelectedOrgMenu;
